import React, { useEffect, useRef } from "react";

const GOOGLE_MAPS_API_KEY = "AIzaSyCbVLHDKmRETk7xp5RZzUdMYPCZe7Is59w";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };

const loadGoogleMapsScript = (onLoad) => {
    if (window.google && window.google.maps) {
        onLoad();
        return;
    }
    const existingScript = document.getElementById("google-maps-js");
    if (existingScript) {
        existingScript.addEventListener("load", onLoad);
        return;
    }
    const script = document.createElement("script");
    script.id = "google-maps-js";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = onLoad;
    document.body.appendChild(script);
};

// Renders only the selected rows as markers, and stays in sync as the
// selection changes (adds new pins, removes ones that get unchecked)
// without ever re-creating the underlying map.
const ConsigneesMapView = ({ rows, isOpen, onToggle }) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const infoWindowRef = useRef(null);
    const markersRef = useRef({});
    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    const syncMarkers = () => {
        const map = mapRef.current;
        if (!map) return;

        const currentRows = rowsRef.current;
        const selectedCodes = new Set(currentRows.map((row) => row.consignee_code));

        Object.keys(markersRef.current).forEach((code) => {
            if (!selectedCodes.has(code)) {
                markersRef.current[code].setMap(null);
                delete markersRef.current[code];
            }
        });

        currentRows.forEach((row) => {
            if (!markersRef.current[row.consignee_code]) {
                const marker = new window.google.maps.Marker({
                    position: { lat: parseFloat(row.lat), lng: parseFloat(row.lng) },
                    map,
                    title: row.consignee_name
                });
                marker.addListener("click", () => {
                    infoWindowRef.current.setContent(
                        `<div style="font-size:12px"><b>${row.consignee_name}</b><br/>${row.consignee_code}<br/>${row.city || ""} ${row.state || ""}</div>`
                    );
                    infoWindowRef.current.open(map, marker);
                });
                markersRef.current[row.consignee_code] = marker;
            }
        });

        const markerList = Object.values(markersRef.current);
        if (markerList.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            markerList.forEach((marker) => bounds.extend(marker.getPosition()));
            map.fitBounds(bounds);
            if (markerList.length === 1) {
                map.setZoom(12);
            }
        } else {
            map.setCenter(DEFAULT_CENTER);
            map.setZoom(5);
        }
    };

    useEffect(() => {
        loadGoogleMapsScript(() => {
            if (!mapRef.current && containerRef.current) {
                mapRef.current = new window.google.maps.Map(containerRef.current, {
                    zoom: 5,
                    center: DEFAULT_CENTER,
                    mapTypeControl: true,
                    streetViewControl: true,
                    fullscreenControl: true
                });
                infoWindowRef.current = new window.google.maps.InfoWindow();
            }
            syncMarkers();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (mapRef.current) {
            syncMarkers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    // The map container is kept mounted (not removed) while collapsed, just hidden via
    // display:none, so the Google Maps instance never has to be recreated on reopen.
    // Re-trigger a resize + refit here since Maps can mis-render tiles after being
    // revealed from a zero-size/hidden container.
    useEffect(() => {
        if (isOpen && mapRef.current && window.google) {
            window.google.maps.event.trigger(mapRef.current, "resize");
            syncMarkers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return (
        <div className="mb-3" style={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", flex: isOpen ? "1 1 50%" : "0 0 auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div
                className="d-flex justify-content-between align-items-center"
                style={{ padding: "8px 14px", background: "#f5f7fa", borderBottom: "1px solid #e0e0e0", flexShrink: 0, cursor: "pointer" }}
                onClick={onToggle}
            >
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#1a237e" }}>
                    Selected Consignees on Map ({rows.length})
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", fontWeight: 600, color: "#1976d2" }}>
                    {isOpen ? "Close" : "Open"}
                    <span style={{ display: "inline-block", fontSize: "10px" }}>
                        {isOpen ? "▲" : "▼"}
                    </span>
                </span>
            </div>
            <div
                ref={containerRef}
                style={{ width: "100%", flex: isOpen ? 1 : 0, minHeight: 0, display: isOpen ? "block" : "none" }}
            />
        </div>
    );
};

export default ConsigneesMapView;